import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { Box, Flex, Heading, Link, Text } from "@chakra-ui/react";

import { useAuthentication, SIGNIN_NOTICE_KEY } from "../../wrappers/UseAuthentication";
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext";
import { usePageAccess } from "../../wrappers/PageAccessContext";

import "./signin.css";
import LandingNavbar from "../landing/components/LandingNavbar";
import ParticleCanvas from "./components/ParticleCanvas";
import {
  EnvelopeIcon,
  FloatInput,
  MiniFooter,
  PasswordInput,
  RetailerStrip,
  SubmitBtn,
  Toast,
  isValidEmail,
} from "./components/SigninAtoms";
import { FaEnvelopeOpenText } from "react-icons/fa6";
import { getDeviceId } from "../../tools/deviceId";

const ACCENT = "#04aec1";
const ACCENT_MID = "#6366f1";
const ACCENT_END = "#ec4899";

// Full-cover background for the left hero panel. The image already contains
// the engraved Spear · MANAGEMENT wordmark, so no overlaid wordmark is needed.
// Swap to another AI variant by changing this single line:
//   /spearbi_ai1.jpg  /spearbi_ai1.png  /spearbi_ai2.png  /spearbi_ai3.png
const HERO_BG_IMAGE = "/spearbi_ai3.png";

const SignIn = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const location = useLocation();
  const navigate = useNavigate();

  const { signIn, isAuthenticated, restoring } = useAuthentication();
  const { fetchUserAccess, resetContext: resetSupplierRetailerContext } = useSupplierRetailer();
  const { fetchPageAccess, resetContext: resetPageAccessContext, restrictedyn } = usePageAccess();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Always remembered (2026-10-15, Michael: "I want it to last forever"). Every
  // sign-in mints the 30-day sliding refresh token, so a user who opens Spear at
  // least once a month never sees this page again. Switching a user off still
  // ends it: the API checks users.activeyn on every request and on refresh.
  const remember = true;
  const [submitState, setSubmitState] = useState("idle"); // idle | loading | success
  const [toast, setToast] = useState(null);

  // Sign-in mode: 'password' = traditional username+password,
  //                'code'    = email-based verification code (6-digit),
  //                'reset'   = forgot-password flow (code + new password).
  const [mode, setMode] = useState("password");
  const [code, setCode] = useState("");
  const [codeCooldown, setCodeCooldown] = useState(0); // seconds until resend allowed
  const [resetStep, setResetStep] = useState(1); // 1 = request reset code, 2 = enter code + new password
  const [newPassword, setNewPassword] = useState("");

  // Auto-dismiss toast — 8s by default so users have time to read it. Errors
  // hang around a bit longer so they don't blink past someone trying to
  // figure out what went wrong.
  useEffect(() => {
    if (!toast) return;
    const ttl = toast.status === "error" ? 9000 : 7000;
    const t = setTimeout(() => setToast(null), ttl);
    return () => clearTimeout(t);
  }, [toast]);

  // Why the app brought you here (session ended, account switched off, account
  // would not load) — set by AuthProvider, shown once.
  useEffect(() => {
    try {
      const notice = sessionStorage.getItem(SIGNIN_NOTICE_KEY);
      if (notice) {
        sessionStorage.removeItem(SIGNIN_NOTICE_KEY);
        setToast({ status: "error", title: "Please sign in", description: notice });
      }
    } catch (e) {
      // ignore — sessionStorage may be unavailable
    }
  }, []);

  // Tick down the resend cooldown.
  useEffect(() => {
    if (codeCooldown <= 0) return;
    const t = setTimeout(() => setCodeCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [codeCooldown]);

  // A remembered device goes straight in (Helena, 2026-10-16: "if a person logs onto the same device each time,
  // log them in immediately instead of making them click log in"). AuthProvider restores the session from the
  // refresh token on every cold load, also when the page opened is /signin (the website's Login / Sign in buttons,
  // a bookmark, a ?redirect= link). Once it has, leave the form for where they were going. Sign out still
  // forgets the device, so a signed-out device stays on this page.
  useEffect(() => {
    if (restoring || !isAuthenticated) return;
    navigate(signInDestination(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoring, isAuthenticated]);

  // Honor existing autoSignIn behavior: redirect-param case.
  useEffect(() => {
    const autoSignIn = async () => {
      const ls_staySignedIn = localStorage.getItem("staySignedIn") === "true";
      if (ls_staySignedIn) return;

      const ls_username = localStorage.getItem("username");
      const ls_password = localStorage.getItem("password");
      const ls_userid = localStorage.getItem("userid");
      const searchParams = new URLSearchParams(location.search);
      const redirect = searchParams.get("redirect");

      if (ls_username && ls_password && redirect) {
        if (ls_userid) fetchPageAccess(ls_userid);
        setEmail(ls_username);
        setPassword(ls_password);
        await runAuth(ls_username, ls_password, `/${redirect}`);
      }
    };
    autoSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Shared success path used by every sign-in method (password, code, Google).
  // refreshToken (when present and remember=true) replaces the legacy
  // plaintext-password replay; we no longer write `password` to localStorage.
  const handleAuthSuccess = async (data, { page, identifier = null, refreshToken = null, accessToken = null }) => {
    const userid = data[0].userid;
    const fname = data[0].fname;
    const user_type = data[0].user_type || "default";

    if (identifier) localStorage.setItem("username", identifier);
    // Persist the choice on every sign-in, not only when the toggle is
    // clicked — otherwise a user who leaves the default alone never gets it.
    localStorage.setItem("staySignedIn", remember);
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
      // Refresh token replaces password replay — purge any legacy plaintext.
      localStorage.removeItem("password");
    } else {
      // remember=false, or the API declined to mint one. Either way there is
      // nothing to restore later, so don't leave an older token lying around.
      localStorage.removeItem("refreshToken");
    }
    localStorage.setItem("userid", userid);
    localStorage.setItem("fname", fname);
    localStorage.setItem("user_type", user_type);
    // Per-request bearer credential. Stored BEFORE the access/page fetches below
    // so those (now authenticated) calls carry it.
    if (accessToken) localStorage.setItem("accessToken", accessToken);

    const okUser = await fetchUserAccess(userid);
    const okPages = await fetchPageAccess(userid);
    if (!okUser || !okPages) {
      // Signed in, but the account would not load — never open a blank app.
      setSubmitState("idle");
      setToast({
        status: "error",
        title: "Sign in Failed!",
        description: "We couldn't load your account. Please check your connection and try again.",
      });
      return;
    }

    setSubmitState("success");
    setToast({ status: "success", title: "Welcome back", description: "Looks good!" });
    signIn(user_type);
    setTimeout(() => navigate(page), 900);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitState !== "idle") return;

    if (mode === "code") {
      await handleVerifyCode();
      return;
    }
    if (mode === "reset") {
      if (resetStep === 1) await handleRequestResetCode();
      else await handleResetPassword();
      return;
    }

    if (!email.trim() || !password) {
      setToast({
        status: "error",
        title: "Sign in Failed!",
        description: "Please enter both your username/email and password.",
      });
      return;
    }
    resetSupplierRetailerContext();
    resetPageAccessContext();
    await runAuth(email.trim(), password, signInDestination());
  };

  // RequireAuth appends ?redirect=<path> when it bounces you to sign in, but
  // only the auto-sign-in branch below ever read it — signing in by hand always
  // dropped you on /sales, losing the page you asked for.
  const signInDestination = () => {
    if (restrictedyn) return "/myProfile";
    const redirect = new URLSearchParams(location.search).get("redirect");
    // Same-origin paths only; never follow an absolute URL from the query.
    if (redirect && /^[A-Za-z0-9\-_/]+$/.test(redirect)) return `/${redirect.replace(/^\//, "")}`;
    return "/sales";
  };

  const runAuth = async (username, pwd, page) => {
    setSubmitState("loading");
    try {
      const response = await axios.post(`${apiUrl}/login/authenticate`, {
        username,
        password: pwd,
        remember,
        // Anonymous per-browser id, for the master-only Login History page.
        deviceId: getDeviceId(),
      });
      if (response.status === 201) {
        await handleAuthSuccess(response.data.data, {
          page,
          identifier: username,
          refreshToken: response.data.refreshToken,
          accessToken: response.data.accessToken,
        });
      } else {
        setSubmitState("idle");
        setToast({
          status: "error",
          title: response.data.message || "Sign in Failed!",
          description: response.data.description || "Please check your credentials.",
        });
      }
    } catch (error) {
      setSubmitState("idle");
      const errMsg =
        error.response?.data?.data?.error ||
        error.response?.data?.message ||
        (!error.response ? "Couldn't reach Spear. Please check your connection and try again." : "Technical Error Occurred");
      setToast({
        status: "error",
        title: "Sign in Failed!",
        description: errMsg,
      });
    }
  };

  // Email-based sign-in code flow. Sends a 6-digit
  // numeric code to users.useremail's first address.
  const handleRequestCode = async () => {
    if (submitState !== "idle" || codeCooldown > 0) return;
    if (!isValidEmail(email)) {
      setToast({
        status: "error",
        title: "Email needed",
        description: "Enter the email associated with your account.",
      });
      return;
    }
    setSubmitState("loading");
    try {
      const response = await axios.post(`${apiUrl}/login/request-code`, { email: email.trim() });
      setSubmitState("idle");
      const cooldown = response.data?.cooldown || 60;
      setCodeCooldown(cooldown);
      setMode("code");
      setCode("");
      setToast({
        status: "info",
        title: "Code sent",
        description: `If ${email.trim()} is on file, a 6-digit code is on its way.`,
      });
    } catch (error) {
      setSubmitState("idle");
      setToast({
        status: "error",
        title: "Couldn't send code",
        description: error.response?.data?.message || "Try again in a moment.",
      });
    }
  };

  const handleVerifyCode = async () => {
    if (submitState !== "idle") return;
    if (!isValidEmail(email)) {
      setToast({ status: "error", title: "Email missing", description: "Go back and enter your email." });
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setToast({
        status: "error",
        title: "Enter the 6-digit code",
        description: "Check your email for the code we just sent.",
      });
      return;
    }
    resetSupplierRetailerContext();
    resetPageAccessContext();
    setSubmitState("loading");
    try {
      const response = await axios.post(`${apiUrl}/login/verify-code`, {
        email: email.trim(),
        code,
        remember,
        deviceId: getDeviceId(),
      });
      if (response.status === 201) {
        await handleAuthSuccess(response.data.data, {
          page: signInDestination(),
          identifier: email.trim(),
          refreshToken: response.data.refreshToken,
          accessToken: response.data.accessToken,
        });
      } else {
        setSubmitState("idle");
        setToast({
          status: "error",
          title: response.data.message || "Invalid code",
          description: response.data.description || "Request a new code and try again.",
        });
      }
    } catch (error) {
      setSubmitState("idle");
      setToast({
        status: "error",
        title: "Couldn't verify code",
        description: error.response?.data?.message || "Try again.",
      });
    }
  };

  const switchToPasswordMode = () => {
    if (submitState !== "idle") return;
    setMode("password");
    setCode("");
    setNewPassword("");
    setResetStep(1);
  };

  // Forgot-password — step 1 (request code). Accepts a username OR an email
  // (backend resolves either). On confirmed match, advances to step 2; on
  // 202 (no match / inactive / no email on file) it stays on step 1 with a
  // clear toast.
  const handleRequestResetCode = async () => {
    if (submitState !== "idle" || codeCooldown > 0) return;
    if (!email.trim()) {
      setToast({ status: "error", title: "Email or username needed", description: "Enter the email or username associated with your account." });
      return;
    }
    setSubmitState("loading");
    try {
      const response = await axios.post(`${apiUrl}/login/forgot-password`, { email: email.trim() });
      setSubmitState("idle");
      if (response.status !== 201) {
        setToast({
          status: "error",
          title: response.data?.message || "Couldn't send reset code",
          description: response.data?.description || "",
        });
        return;
      }
      const cooldown = response.data?.cooldown || 60;
      const sentTo = response.data?.emailSentTo;
      setCodeCooldown(cooldown);
      setResetStep(2);
      setCode("");
      setToast({
        status: "success",
        title: "Reset code sent",
        description: sentTo
          ? `We sent a 6-digit code to ${sentTo}. It can take a moment to arrive.`
          : "Check your email for a 6-digit code (it can take a moment to arrive).",
      });
    } catch (error) {
      setSubmitState("idle");
      setToast({ status: "error", title: "Couldn't send reset code", description: error.response?.data?.message || "Try again." });
    }
  };

  // Forgot-password — step 2 (apply new password). Hits /login/reset-password
  // and on success returns the user to password-mode so they can sign in.
  const handleResetPassword = async () => {
    if (submitState !== "idle") return;
    if (!/^\d{6}$/.test(code)) {
      setToast({ status: "error", title: "Enter the 6-digit code", description: "Check your email." });
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setToast({ status: "error", title: "Password too short", description: "Use at least 8 characters." });
      return;
    }
    setSubmitState("loading");
    try {
      const response = await axios.post(`${apiUrl}/login/reset-password`, {
        email: email.trim(),
        code,
        newPassword,
      });
      if (response.status === 201) {
        setSubmitState("idle");
        setToast({ status: "success", title: "Password updated", description: "Sign in with your new password." });
        setMode("password");
        setResetStep(1);
        setCode("");
        setPassword(newPassword);
        setNewPassword("");
      } else {
        setSubmitState("idle");
        setToast({ status: "error", title: response.data.message || "Reset failed", description: "" });
      }
    } catch (error) {
      setSubmitState("idle");
      setToast({ status: "error", title: "Couldn't reset password", description: error.response?.data?.message || "Try again." });
    }
  };

  const switchToResetMode = () => {
    if (submitState !== "idle") return;
    setMode("reset");
    setResetStep(1);
    setCode("");
    setNewPassword("");
  };

  return (
    <Box position="relative" minH="100vh" bg="#ffffff" overflow="hidden">
      {/* Navbar overlays the split — panels stretch to the top edge of the
          viewport, navbar floats in front via z-index. */}
      <Box position="absolute" top={0} left={0} right={0} zIndex={100}>
        <LandingNavbar />
      </Box>
      <Box
        minH="100vh"
        display="grid"
        gridTemplateColumns={{ base: "1fr", lg: "1.05fr 1fr" }}
        gridTemplateRows={{ base: "220px 1fr", lg: "1fr" }}
        width="100%"
      >
        {/* ─────────────── LEFT — brand hero ─────────────── */}
        <Flex
          position="relative"
          overflow="hidden"
          bg={ACCENT}
          bgImage={`url('${HERO_BG_IMAGE}')`}
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          direction="column"
          justify="space-between"
          padding={{ base: "20px", md: "36px" }}
          paddingTop={{ base: "100px", md: "110px" }}
        >
          {/* Particle field — sits over the bg image */}
          <ParticleCanvas tint="#ffffff" density={1.1} link={120} />

          {/* Top + bottom dark scrim — anchors the white text on the bright
              glass scene without obscuring the engraved wordmark in the centre */}
          <Box
            position="absolute"
            inset={0}
            bg="linear-gradient(180deg, rgba(10,14,26,0.45) 0%, rgba(10,14,26,0) 28%, rgba(10,14,26,0) 70%, rgba(10,14,26,0.5) 100%)"
            pointerEvents="none"
          />

          {/* Top row: need help (right-aligned) */}
          <Flex
            position="relative"
            zIndex={2}
            justify="flex-end"
            align="center"
            display={{ base: "none", sm: "flex" }}
          >
            <Link
              as={RouterLink}
              to="/contact-us"
              fontSize="11px"
              color="rgba(255,255,255,0.85)"
              fontWeight={600}
              _hover={{ color: "white", textDecoration: "none" }}
            >
              Need help?
            </Link>
          </Flex>

          {/* Spacer — lets the engraved wordmark in the bg image breathe */}
          <Box flex="1" />

          {/* Bottom: headline + sub-copy + retailer trust strip */}
          <Box position="relative" zIndex={2} display="flex" flexDirection="column" gap="20px">
            <Box display={{ base: "none", lg: "block" }}>
              <Heading
                as="h2"
                fontSize="26px"
                fontWeight={700}
                letterSpacing="-0.02em"
                lineHeight="1.15"
                maxW="380px"
                color="white"
                textShadow="0 2px 12px rgba(0,0,0,0.35)"
              >
                Sales reporting, decoded.
              </Heading>
              <Text
                mt="10px"
                fontSize="13px"
                color="rgba(255,255,255,0.92)"
                maxW="360px"
                lineHeight="1.55"
                textShadow="0 1px 6px rgba(0,0,0,0.4)"
              >
                Every store · every SKU · every retailer feed — connected in one frosted-glass dashboard.
              </Text>
            </Box>
            <Box display={{ base: "none", md: "block" }}>
              <RetailerStrip dark={true} compact />
            </Box>
          </Box>
        </Flex>

        {/* ─────────────── RIGHT — form panel ─────────────── */}
        <Flex
          position="relative"
          direction="column"
          padding={{ base: "20px", md: "36px" }}
          paddingTop={{ base: "100px", md: "110px" }}
          bg="#ffffff"
          overflow="hidden"
        >
          {/* Soft radial accents on the white panel — fills empty space
              without competing with the form. */}
          <Box
            position="absolute"
            top="-180px"
            right="-160px"
            w="500px"
            h="500px"
            borderRadius="50%"
            bg={`${ACCENT}1f`}
            filter="blur(80px)"
            pointerEvents="none"
          />
          <Box
            position="absolute"
            bottom="-200px"
            left="-180px"
            w="460px"
            h="460px"
            borderRadius="50%"
            bg={`${ACCENT_END}14`}
            filter="blur(80px)"
            pointerEvents="none"
          />
          <Box
            position="absolute"
            top="40%"
            left="-220px"
            w="380px"
            h="380px"
            borderRadius="50%"
            bg={`${ACCENT_MID}14`}
            filter="blur(90px)"
            pointerEvents="none"
          />

          {/* Top-right: New to Spearbi? Book a demo */}
          <Flex
            position="relative"
            zIndex={2}
            justify="flex-end"
            align="center"
            gap="10px"
          >
            <Text fontSize="12px" color="rgba(15,22,38,0.6)">
              New to Spearbi?
            </Text>
            <Link
              as={RouterLink}
              to="/contact-us"
              fontSize="12px"
              fontWeight={700}
              color={ACCENT}
              padding="6px 12px"
              borderRadius="9999px"
              border={`1px solid ${ACCENT}55`}
              bg="rgba(255,255,255,0.7)"
              backdropFilter="blur(8px)"
              _hover={{ bg: `${ACCENT}12`, textDecoration: "none" }}
            >
              Book a demo
            </Link>
          </Flex>

          {/* Glass card wrapping the form */}
          <Flex
            flex="1"
            justify="center"
            align="center"
            position="relative"
            zIndex={2}
            py="24px"
            overflowY="auto"
          >
            <Box
              as="form"
              onSubmit={handleSubmit}
              position="relative"
              maxW="400px"
              width="100%"
              padding={{ base: "24px", md: "32px" }}
              bg="rgba(255,255,255,0.7)"
              border="1px solid rgba(15,22,38,0.08)"
              borderRadius="22px"
              backdropFilter="blur(22px) saturate(160%)"
              boxShadow="0 30px 60px -20px rgba(15,22,38,0.18), 0 10px 30px -15px rgba(4,174,193,0.18), inset 0 1px 0 rgba(255,255,255,0.6)"
              display="flex"
              flexDirection="column"
              gap="14px"
              sx={{
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: "28px",
                  right: "28px",
                  top: 0,
                  height: "1px",
                  background: `linear-gradient(90deg, transparent, ${ACCENT}, ${ACCENT_MID}, ${ACCENT_END}, transparent)`,
                  opacity: 0.75,
                },
              }}
            >
            <Toast toast={toast} dark={false} />

            <Box>
              <Heading
                as="h1"
                margin={0}
                fontSize="28px"
                fontWeight={700}
                letterSpacing="-0.025em"
                color="#0a0e1a"
              >
                {mode === "code"
                  ? "Enter your code"
                  : mode === "reset"
                  ? resetStep === 1
                    ? "Reset your password"
                    : "Set a new password"
                  : "Sign in"}
              </Heading>
              <Text
                margin="6px 0 0"
                fontSize="13px"
                color="rgba(15,22,38,0.6)"
              >
                {mode === "code"
                  ? `We sent a 6-digit code to ${email}.`
                  : mode === "reset"
                  ? resetStep === 1
                    ? "Enter your email or username — we'll send a 6-digit code to the email on file."
                    : "Enter the 6-digit code plus a new password."
                  : "Pick up where you left off."}
              </Text>
            </Box>

            {mode === "password" && (
              <>
                <FloatInput
                  label="Email or username"
                  value={email}
                  onChange={setEmail}
                  type="text"
                  icon={<EnvelopeIcon />}
                  valid={email.length > 0 && isValidEmail(email)}
                  accent={ACCENT}
                  dark={false}
                  autoComplete="username"
                />

                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  accent={ACCENT}
                  dark={false}
                />

                <Flex justify="space-between" align="center" fontSize="12px">
                  <Box />
                  <Box
                    as="button"
                    type="button"
                    onClick={switchToResetMode}
                    background="transparent"
                    border="none"
                    color={ACCENT}
                    fontWeight={600}
                    cursor="pointer"
                    padding="0"
                    _hover={{ opacity: 0.8 }}
                  >
                    Forgot password?
                  </Box>
                </Flex>

                <SubmitBtn
                  state={submitState}
                  accent={ACCENT}
                  accentMid={ACCENT_MID}
                  accentEnd={ACCENT_END}
                />

                <Box
                  as="button"
                  type="button"
                  onClick={handleRequestCode}
                  background="transparent"
                  border="none"
                  color="rgba(15,22,38,0.6)"
                  fontSize="12px"
                  fontWeight={500}
                  cursor="pointer"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap="6px"
                  padding="8px"
                  transition="color .2s cubic-bezier(0.4, 0, 0.2, 1)"
                  _hover={{ color: "#0a0e1a" }}
                >
                  <Box as="span" color={ACCENT} display="grid" placeItems="center" fontSize="11px">
                    <FaEnvelopeOpenText />
                  </Box>
                  Email me a sign-in code
                </Box>
              </>
            )}

            {mode === "code" && (
              <>
                <FloatInput
                  label="6-digit code"
                  value={code}
                  onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
                  type="text"
                  accent={ACCENT}
                  dark={false}
                  autoComplete="one-time-code"
                  ariaLabel="6-digit code"
                />

                <SubmitBtn
                  state={submitState}
                  label="Verify and sign in"
                  loadingLabel="Verifying…"
                  accent={ACCENT}
                  accentMid={ACCENT_MID}
                  accentEnd={ACCENT_END}
                />

                <Flex justify="space-between" align="center" fontSize="12px">
                  <Box
                    as="button"
                    type="button"
                    onClick={switchToPasswordMode}
                    background="transparent"
                    border="none"
                    color="rgba(15,22,38,0.6)"
                    fontWeight={500}
                    cursor="pointer"
                    padding="0"
                    _hover={{ color: "#0a0e1a" }}
                  >
                    ← Use password instead
                  </Box>
                  <Box
                    as="button"
                    type="button"
                    onClick={handleRequestCode}
                    background="transparent"
                    border="none"
                    color={codeCooldown > 0 ? "rgba(15,22,38,0.4)" : ACCENT}
                    fontWeight={600}
                    cursor={codeCooldown > 0 ? "default" : "pointer"}
                    padding="0"
                    disabled={codeCooldown > 0}
                  >
                    {codeCooldown > 0 ? `Resend in ${codeCooldown}s` : "Resend code"}
                  </Box>
                </Flex>
              </>
            )}

            {mode === "reset" && resetStep === 1 && (
              <>
                <FloatInput
                  label="Email or username"
                  value={email}
                  onChange={setEmail}
                  type="text"
                  icon={<EnvelopeIcon />}
                  valid={email.length > 0 && isValidEmail(email)}
                  accent={ACCENT}
                  dark={false}
                  autoComplete="username"
                />
                <SubmitBtn
                  state={submitState}
                  label="Send reset code"
                  loadingLabel="Sending…"
                  accent={ACCENT}
                  accentMid={ACCENT_MID}
                  accentEnd={ACCENT_END}
                />
                <Box
                  as="button"
                  type="button"
                  onClick={switchToPasswordMode}
                  background="transparent"
                  border="none"
                  color="rgba(15,22,38,0.6)"
                  fontSize="12px"
                  fontWeight={500}
                  cursor="pointer"
                  padding="8px"
                  textAlign="center"
                  _hover={{ color: "#0a0e1a" }}
                >
                  ← Back to sign in
                </Box>
              </>
            )}

            {mode === "reset" && resetStep === 2 && (
              <>
                <FloatInput
                  label="6-digit code"
                  value={code}
                  onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
                  type="text"
                  accent={ACCENT}
                  dark={false}
                  autoComplete="one-time-code"
                  ariaLabel="6-digit code"
                />
                <PasswordInput
                  value={newPassword}
                  onChange={setNewPassword}
                  accent={ACCENT}
                  dark={false}
                  label="New password (8+ characters)"
                />
                <SubmitBtn
                  state={submitState}
                  label="Update password"
                  loadingLabel="Updating…"
                  accent={ACCENT}
                  accentMid={ACCENT_MID}
                  accentEnd={ACCENT_END}
                />
                <Flex justify="space-between" align="center" fontSize="12px">
                  <Box
                    as="button"
                    type="button"
                    onClick={switchToPasswordMode}
                    background="transparent"
                    border="none"
                    color="rgba(15,22,38,0.6)"
                    fontWeight={500}
                    cursor="pointer"
                    padding="0"
                    _hover={{ color: "#0a0e1a" }}
                  >
                    ← Cancel
                  </Box>
                  <Box
                    as="button"
                    type="button"
                    onClick={handleRequestResetCode}
                    background="transparent"
                    border="none"
                    color={codeCooldown > 0 ? "rgba(15,22,38,0.4)" : ACCENT}
                    fontWeight={600}
                    cursor={codeCooldown > 0 ? "default" : "pointer"}
                    padding="0"
                    disabled={codeCooldown > 0}
                  >
                    {codeCooldown > 0 ? `Resend in ${codeCooldown}s` : "Resend code"}
                  </Box>
                </Flex>
              </>
            )}
          </Box>
          </Flex>

          <MiniFooter
            dark={false}
            onPrivacy={() => navigate("/privacy")}
            onTerms={() => navigate("/terms")}
            onHelp={() => navigate("/contact-us")}
          />
        </Flex>
      </Box>
    </Box>
  );
};

export default SignIn;
